import React from 'react';

import Card from "@mui/material/Card";
import Grid from '@mui/material/Grid';
import Typography from "@mui/material/Typography";

import IParamScript from '../../database/interfaces/paramScript/IParamScript';
import PrestaGlobals from '../../common/PrestaGlobals';
import { AdminContext } from '../../admin/AdminContext';
import { formatParamRef, formatMargin } from '../../admin/paramUtils';
import CountCardContent from './CountCardContent';
import DecorCardContent from './DecorCardContent';
import ValueReset from '../../common/inputs/ValueReset';
import BoolCardContent from './BoolCardContent';
import BackPassingCardContent from './BackPassingCardContent';
import HardwareCardContent from './HardwareCardContent';
import DrawerSystemCardContent from './DrawerSystemCardContent';
import ItemsCardContent from './ItemsCardContent';
import RailingCardContent from './RailingCardContent';
import EnumCardContent from './EnumCardContent';
import SizeCardContent from './SizeCardContent';
import TextCardContent from './TextCardContent';

import { LayoutContext } from '../../layout/LayoutHandler';
import { DatabaseContext } from '../../database/DatabaseHandler';
import { ParametersContext } from '../../parametersProcessor/ParametersProcessor';
import { isParamVisible } from '../../parametersProcessor/paramVisibility';
import { resolveItems } from '../../parametersProcessor/paramItems';
import { resolveParamDefault } from '../../parametersProcessor/paramDefaults';
import { ICountParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { IDecorParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { IEnumParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { IBoolParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { IBackPassingParamTypeDescriptor, IDrawerSystemParamTypeDescriptor, IHardwareParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { IRailingParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { ISizeParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { ITextParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';

/**
 * Base class for the cabinet property container cards.
 */
interface IPropertyContainerCardProps {
    title: string;
    properties: IParamScript[];
}

/**
 * Resolve the parameter key the way ParametersProcessor stores it: indexed properties
 * are keyed as `Property[index]`. Reads and writes must both use this resolved key,
 * otherwise indexed values (e.g. the per-zone "Front") are written to `Front[n]` but read
 * back from `Front`, so the field always falls back to its type-descriptor default.
 */
const resolvePropertyKey = (property: IParamScript): string =>
    property.parameterPropertyIndex !== undefined && property.parameterPropertyIndex !== null
        ? `${property.parameterProperty}[${property.parameterPropertyIndex}]`
        : property.parameterProperty;

const PropertyContainerCard: React.FC<IPropertyContainerCardProps> = ({ title, properties }) => {
    // Use the ParametersContext
    const paramsContext = React.useContext(ParametersContext);
    // Use the LayoutContext to get the current container breakpoint
    const layoutContext = React.useContext(LayoutContext);
    // A Decor alapértelmezésének feloldásához kell (a gyári lap NEVE a tooltipbe) — lásd paramDefaults.
    const dataContext = React.useContext(DatabaseContext);
    // Admin mode: lets the configurator surface hidden params and open the descriptor editor on
    // right-click. Outside AdminProvider this resolves to the no-op default context.
    const admin = React.useContext(AdminContext);
    const isAdmin = PrestaGlobals.isAdmin;

    // A property-t magát adjuk át (nem indexet): a filteredProperties (visibleWhen/hidden szűrés után)
    // indexei NEM egyeznek a `properties` eredeti tömbjének indexeivel, egy pozíció-alapú lookup a
    // rossz mezőt módosítaná, amint egy csoportban valamelyik property ki van szűrve.
    const onUpdateParamValue = React.useCallback((property: IParamScript, value: string) => {
        paramsContext.updateParameters(property.parameterName, resolvePropertyKey(property), value);
        // EA-485: persist the change as the logged-in customer's per-product default
        // (debounced, login-gated inside notifyParamChanged).
        paramsContext.notifyParamChanged(property);
    }, [paramsContext.updateParameters, paramsContext.notifyParamChanged]);

    // Adat-vezérelt feltételes láthatóság: a property csak akkor renderelődik, ha a leírójában
    // megadott MÁSIK paraméter adott mezője az `in` listában szereplő érték egyike (pl. a Railing-dekor
    // csak Single/Double railing-típusnál jelenik meg). Hiányzó/ismeretlen érték esetén a feltétel
    // NEM teljesül (rejtve marad) — így nincs villanás egy alapértelmezetten letiltott mezőre.
    // A kiértékelő MEGOSZTOTT a Unity-kimenettel (ParametersProcessor.generateStyleScript is ezzel
    // szűr), hogy a látszó mezők és a kiküldött stylescript ne csúszhasson el egymástól.
    const isVisible = React.useCallback(
        (property: IParamScript): boolean =>
            isParamVisible(paramsContext.parameters, property.typeDescriptor?.visibleWhen),
        [paramsContext.parameters]
    );

    // Customers never see hidden params; admins see all of them (marked) so they can edit/right-click.
    const filteredProperties = (isAdmin
        ? properties
        : properties.filter(property => !property.typeDescriptor?.hidden)
    ).filter(isVisible);

    // Ha MINDEN mező kiszűrődött (rejtett vagy a `visibleWhen` nem teljesül), a kártyát SEM rajzoljuk ki
    // — különben egy üres, csak feliratos kártya maradna (pl. a "Keresztmerevítő anyaga" és
    // "…szélessége" kártyák "Nincs" merevítés esetén). Egy mező nélküli kártya sosem hordoz információt.
    if (!filteredProperties.length) return null;

    return (
        <Card sx={{ margin: 1, padding: 1}} >
            {title !== "" && (
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, mb: 1 }}>
                    {title}
                </Typography>
            )}
            <Grid container spacing={1.5} direction="column">
                {filteredProperties.map((property, index) => {
                    // `??` és NEM `||`: a `||` a HAMIS de LÉTEZŐ értékeket is üresre cserélné — a boolean
                    // `false`-ot (Bool kártya) és a numerikus 0-t is. Egy `default: true` descriptornál a
                    // `||` azt okozná, hogy a kikapcsolás visszapattan: a state `false`-t tart, a kártya
                    // viszont ""-t kap, arra az alapértéket (true) mutatja. A meglévő típusokon a csere
                    // NEM változtat semmit: a Size/Text értékei stringek ("600mm", ""), a Decor/Railing/
                    // BackPassing objektumok, a Count-nál pedig a 0 ugyanúgy 0-ként jelenik meg
                    // (parseInt(""), illetve parseInt("0") után is `value || 0`).
                    let paramValue = paramsContext.parameters?.[property.parameterName]?.[resolvePropertyKey(property)] ?? "";
                    
                    return (
                        <Grid
                            key={property.id}
                            size={{ xs: 12, sm: 12, lg: 12 }}
                            onContextMenu={isAdmin ? (e) => { e.preventDefault(); admin.openDescriptorEditor(property); } : undefined}
                            title={isAdmin ? 'Jobb klikk: paraméter szerkesztése' : undefined}
                            // TODO: reconsider, in this context it just did not fit to the design.
                            // size={{ xs: (
                            //     filteredProperties.length === 1
                            //     ?
                            //     12 // One property always takes the full width
                            //     :
                            //     (filteredProperties.length === 2
                            //         ? // Two properties maximum split the width
                            //         (
                            //             layoutContext.containerBp === "xs" ? 12 : 6
                            //         )
                            //         :
                            //         (filteredProperties.length === 3
                            //             ? // Three properties split the width in maximum thirds
                            //             (
                            //                 layoutContext.containerBp === "xs" ? 12
                            //                 : (layoutContext.containerBp === "sm" ? 6 : 4)
                            //             )
                            //             : // More than three properties split the width in maximum quarters
                            //             (
                            //                 layoutContext.containerBp === "xs" ? 12
                            //                 : (layoutContext.containerBp === "sm" ? 6
                            //                 : (layoutContext.containerBp === "md" ? 4 : 3)))
                            //             )
                            //         )
                            //     )
                            // }}
                        >
                            {/* A "visszaállítás alapértelmezettre" gomb EGYETLEN bekötési pontja: a
                                kártya-tartalom változatlanul renderelődik, a slot csak MELLÉ teszi az
                                ikont, ha a paraméterhez van feloldható alapértelmezés. A visszaállítás
                                ugyanazon az onUpdateParamValue úton megy, mint bármelyik kézi
                                szerkesztés — nincs külön írási ág. */}
                            <ValueReset
                                paramDefault={resolveParamDefault(property.parameterType, property.typeDescriptor, {
                                    storedDefault: property.defaultValue,
                                    currentValue: paramValue,
                                    findBoard: (id: string) => dataContext.boardData?.findByID(id) || null,
                                })}
                                currentValue={paramValue}
                                onReset={(newValue) => onUpdateParamValue(property, newValue)}
                            >
                            {(property.typeDescriptor != null
                            ?
                            (() => {
                                // ADAT-VEZÉRELT KÁRTYA-VÁLASZTÁS: ha a descriptor `fields` tömbben sorolja
                                // fel a mezőit, a generikus composite kártya rendereli — a paraméter
                                // TÍPUS-NEVÉTŐL függetlenül. Így egy új objektum-értékű típus (CabinetTilt,
                                // és a DB-ben már felvett QuadrantArcCutout / Rounding / ToeKick /
                                // Drilling / DrillSeries) bekötése CSAK descriptor-adat.
                                // A nevesített ágak ERŐSEBBEK maradnak: a Railing és a BackPassing
                                // descriptora nem deklarál `fields`-et, tehát ez rájuk nem hat.
                                // ADAT-VEZÉRELT KÁRTYA-VÁLASZTÁS: ha a descriptor TÖBB AL-MEZŐT ír le —
                                // akár nevesített listával (`fields`), akár darabszám-vezérelt
                                // ismétléssel (`countFrom`) —, a közös kártya rendereli, a paraméter
                                // TÍPUS-NEVÉTŐL függetlenül. Így egy új objektum-értékű típus (a DB-ben
                                // már felvett QuadrantArcCutout / Rounding / ToeKick / Drilling /
                                // DrillSeries) bekötése CSAK descriptor-adat.
                                // A nevesített ágak ERŐSEBBEK maradnak: a Railing és a BackPassing
                                // descriptora egyik alakot sem deklarálja, tehát ez rájuk nem hat.
                                if (resolveItems(property.typeDescriptor)) {
                                    return <ItemsCardContent
                                        typeDescriptor={property.typeDescriptor}
                                        paramValue={paramValue}
                                        storedDefault={property.defaultValue}
                                        onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                    />;
                                }

                                switch (property.parameterType) {
                                    case "Count":
                                        return <CountCardContent
                                            typeDescriptor={property.typeDescriptor as ICountParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                            />;
                                    case "Decor":
                                        return <DecorCardContent
                                            typeDescriptor={property.typeDescriptor as IDecorParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Bool":
                                        return <BoolCardContent
                                            typeDescriptor={property.typeDescriptor as IBoolParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "BackPassing":
                                        return <BackPassingCardContent
                                            typeDescriptor={property.typeDescriptor as IBackPassingParamTypeDescriptor}
                                            paramValue={paramValue}
                                            storedDefault={property.defaultValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Railing":
                                        return <RailingCardContent
                                            typeDescriptor={property.typeDescriptor as IRailingParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Hardware":
                                        return <HardwareCardContent
                                            typeDescriptor={property.typeDescriptor as IHardwareParamTypeDescriptor}
                                            paramValue={paramValue}
                                            storedDefault={property.defaultValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "DrawerSystem":
                                        return <DrawerSystemCardContent
                                            typeDescriptor={property.typeDescriptor as IDrawerSystemParamTypeDescriptor}
                                            paramValue={paramValue}
                                            storedDefault={property.defaultValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Enum":
                                        return <EnumCardContent
                                            typeDescriptor={property.typeDescriptor as IEnumParamTypeDescriptor}
                                            paramValue={paramValue}
                                            property={property.parameterProperty}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Size":
                                        return <SizeCardContent
                                            typeDescriptor={property.typeDescriptor as ISizeParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    case "Text":
                                        return <TextCardContent
                                            typeDescriptor={property.typeDescriptor as ITextParamTypeDescriptor}
                                            paramValue={paramValue}
                                            onUpdateParamValue={(newValue) => onUpdateParamValue(property, newValue)}
                                        />;
                                    default:
                                        // Types without a card renderer (e.g. Margin) only appear here in admin mode
                                        // (customers never see hidden params). Show a clean read-only line instead of an
                                        // error — these are edited on the Admin panel's "Paraméterek" tab.
                                        return isAdmin ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {property.typeDescriptor?.label || property.parameterName}:{' '}
                                                <code>{property.parameterType === 'Margin' ? formatMargin(String(paramValue)) : String(paramValue)}</code>
                                                <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                                                    {formatParamRef(property)}
                                                    {' · '}{property.parameterType} — Admin „Paraméterek” fül
                                                </Typography>
                                            </Typography>
                                        ) : (
                                            <Typography variant="subtitle1" component="div" sx={{ color: 'error.main' }}>
                                                Unknown property type: {property.parameterType}
                                            </Typography>
                                        );
                                }
                            })()
                            : // If the script does not have a valid type descriptor, show a warning
                            <Typography variant="subtitle1" component="div" sx={{ color: 'error.main' }}>
                                No type descriptor found for: {property.parameterName || "Unknown"}
                                <br />
                                Type: {property.parameterType || "Unknown"}
                            </Typography>
                            )}
                            </ValueReset>
                        </Grid>
                    );
                })}
            </Grid>
        </Card>
    );
};

export default PropertyContainerCard;
